import { useEffect, useState } from 'react'

import { requestApi } from './catalog'
import { clearWebSession, readWebSession, type WebSession } from './web-session'

import './account-center.css'

interface CustomerIdentity {
  provider: 'wechat' | 'phone'
  maskedSubject: string
  verified: boolean
  status: 'active' | 'revoked'
  verifiedAt: string | null
}

interface AccountLinkCreated {
  id: string
  token: string
  expiresAt: string
  expiresInSeconds: number
}

interface DeactivationRequest {
  id: string
  status: 'requested' | 'reviewing' | 'blocked' | 'processing' | 'completed' | 'cancelled'
  blockReason: string | null
  requestedAt: string
}

const SESSION_KEY = 'nail-web.session.v1'

/** 父交易状态文案，与小程序「我的订单」一致。 */
const orderStatusLabels: Record<string, string> = {
  pending_payment: '待支付',
  paying: '支付处理中',
  paid: '已支付',
  closed: '已关闭',
  partial_refunded: '部分退款',
  refunded: '已退款'
}

const deactivationLabels: Record<DeactivationRequest['status'], string> = {
  requested: '已提交，等待门店办理',
  reviewing: '门店正在审核',
  blocked: '暂不能注销：仍有未完成预约',
  processing: '正在办理',
  completed: '已完成注销',
  cancelled: '申请已取消'
}

/**
 * 官网账户中心：手机号验证码登录（验证后身份）、查看已核验身份、
 * 生成微信关联码（在小程序内确认）以及提交注销申请。
 */
export function AccountCenter({ open, onClose, refreshToken = 0 }: {
  open: boolean; onClose: () => void; refreshToken?: number
}) {
  const [session, setSession] = useState<WebSession | null>(() => readWebSession())
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [identities, setIdentities] = useState<CustomerIdentity[]>([])
  const [link, setLink] = useState<AccountLinkCreated | null>(null)
  const [deactivation, setDeactivation] = useState<DeactivationRequest | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  /** R09：官网补齐与小程序一致的资产视图（卡包 / 订单 / 会员与积分）。 */
  const [cards, setCards] = useState<Array<{
    itemId: string; productName: string; skuName: string; expiresAt: string | null
    summary: { total: number; available: number; reserved: number; consumed: number; frozen: number; expired: number }
  }>>([])
  const [orders, setOrders] = useState<Array<{
    id: string; transactionNo: string; status: string; cashTotalCents: number; createdAt: string
  }>>([])
  const [membership, setMembership] = useState<{ level: { name: string; discountBps: number } | null; growth: { value: number } | null } | null>(null)
  const [assets, setAssets] = useState<{ balance: number; settings: { redeemPoints: number; redeemCents: number } } | null>(null)

  const authHeaders = (current: WebSession) => ({ Authorization: `Bearer ${current.accessToken}` })

  const loadAccount = async (current: WebSession) => {
    try {
      const [nextIdentities, requests] = await Promise.all([
        requestApi<CustomerIdentity[]>('/me/identities', { headers: authHeaders(current) }),
        requestApi<DeactivationRequest[]>('/me/deactivation-requests', { headers: authHeaders(current) })
      ])
      setIdentities(nextIdentities)
      setDeactivation(requests[0] ?? null)
      const [cardData, orderData, membershipData, pointsData] = await Promise.all([
        requestApi<{ cards: typeof cards }>('/me/entitlements', { headers: authHeaders(current) }).catch(() => ({ cards: [] })),
        requestApi<typeof orders>('/me/transactions', { headers: authHeaders(current) }).catch(() => []),
        requestApi<typeof membership>('/me/membership', { headers: authHeaders(current) }).catch(() => null),
        requestApi<typeof assets>('/me/points', { headers: authHeaders(current) }).catch(() => null)
      ])
      setCards(cardData.cards ?? [])
      setOrders(orderData ?? [])
      setMembership(membershipData)
      setAssets(pointsData)
    } catch (reason) {
      /**
       * F09：登录态失效时回到“验证码登录”界面，并给出可操作的中文提示；
       * 不再停留在旧的已登录界面显示英文令牌错误。
       */
      if ((reason as { code?: string } | null)?.code === 'SESSION_EXPIRED') {
        clearWebSession()
        setSession(null)
        setCards([])
        setOrders([])
        setIdentities([])
        setMembership(null)
        setAssets(null)
        setError('登录已过期，请重新用手机号验证码登录')
        return
      }
      setError(reason instanceof Error ? reason.message : '账户信息加载失败')
    }
  }

  useEffect(() => {
    if (!open || !session) return
    void loadAccount(session)
  }, [open, session?.accessToken, refreshToken])

  if (!open) return null

  const sendCode = async () => {
    setBusy(true); setError(''); setMessage('')
    try {
      await requestApi('/auth/phone-verifications', {
        method: 'POST',
        body: JSON.stringify({ provider: 'sms', phone })
      })
      setCodeSent(true)
      setMessage('验证码已发送，5 分钟内有效')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '验证码发送失败')
    } finally {
      setBusy(false)
    }
  }

  const login = async () => {
    setBusy(true); setError(''); setMessage('')
    try {
      const next = await requestApi<WebSession>('/auth/sessions', {
        method: 'POST',
        body: JSON.stringify({ provider: 'phone-otp', credential: code, phone })
      })
      window.localStorage.setItem(SESSION_KEY, JSON.stringify(next))
      setSession(next)
      setCode('')
      setMessage('登录成功')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '登录失败')
    } finally {
      setBusy(false)
    }
  }

  const createLink = async () => {
    if (!session) return
    setBusy(true); setError(''); setMessage('')
    try {
      const created = await requestApi<AccountLinkCreated>('/auth/account-links', {
        method: 'POST', body: JSON.stringify({}), headers: authHeaders(session)
      })
      setLink(created)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '关联码生成失败')
    } finally {
      setBusy(false)
    }
  }

  const copyLinkCode = async () => {
    if (!link) return
    const value = `${link.id}.${link.token}`
    try {
      await navigator.clipboard.writeText(value)
      setMessage('关联码已复制')
    } catch {
      setMessage(`请手动复制关联码：${value}`)
    }
  }

  const requestDeactivation = async () => {
    if (!session) return
    setBusy(true); setError(''); setMessage('')
    try {
      const request = await requestApi<DeactivationRequest>('/me/deactivation-requests', {
        method: 'POST', body: JSON.stringify({}), headers: authHeaders(session)
      })
      setDeactivation(request)
      setMessage(request.status === 'blocked' ? '仍有未完成预约，请先处理后再次提交' : '注销申请已提交')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '注销申请提交失败')
    } finally {
      setBusy(false)
    }
  }

  const logout = async () => {
    if (session) {
      try {
        await requestApi('/auth/session', { method: 'DELETE', headers: authHeaders(session) })
      } catch { /* 本地会话仍要清理 */ }
    }
    window.localStorage.removeItem(SESSION_KEY)
    setSession(null); setIdentities([]); setLink(null); setDeactivation(null)
    setMessage('已退出登录')
  }

  const wechat = identities.find((item) => item.provider === 'wechat' && item.verified)
  const phoneIdentity = identities.find((item) => item.provider === 'phone' && item.verified)

  return (
    <div className="account-overlay" role="dialog" aria-modal="true" aria-label="账户中心">
      <div className="account-panel">
        <header>
          <div><span>ACCOUNT CENTER</span><h3>账户中心</h3></div>
          <button type="button" className="account-close" onClick={onClose} aria-label="关闭账户中心">×</button>
        </header>

        {!session ? (
          <div className="account-body">
            <p>使用已验证手机号登录，可查看身份、关联微信小程序账号并管理注销。</p>
            <label>手机号码<input inputMode="numeric" maxLength={11} value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="11 位手机号" /></label>
            <div className="account-row">
              <button type="button" className="account-secondary" disabled={busy || !/^1[0-9]{10}$/.test(phone)} onClick={() => void sendCode()}>获取验证码</button>
              <label className="account-code">验证码<input inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value)} placeholder="6 位验证码" /></label>
            </div>
            <button type="button" className="account-primary" disabled={busy || !codeSent || code.length < 4} onClick={() => void login()}>{busy ? '请稍候…' : '验证码登录'}</button>
            <small>未配置短信通道时无法获取验证码，这是上线前的必备依赖，不会用假验证码替代。</small>
          </div>
        ) : (
          <div className="account-body">
            <div className="account-profile">
              <strong>{session.user.displayName}</strong>
              <span>{session.user.phoneMasked ?? '未绑定手机号'}</span>
            </div>
            <div className="account-block">
              <h4>已核验身份</h4>
              <div className="account-identity"><span>微信</span><b className={wechat ? 'ok' : ''}>{wechat ? '已关联' : '未关联'}</b></div>
              <div className="account-identity"><span>手机号</span><b className={phoneIdentity ? 'ok' : ''}>{phoneIdentity ? phoneIdentity.maskedSubject : '未验证'}</b></div>
            </div>
            {/* R09：套餐卡包与订单在官网同源可见；核销、退款、转赠在小程序内完成（需扫码与支付渠道）。 */}
            <div className="account-block">
              <h4>我的套餐卡包（{cards.length}）</h4>
              {cards.map((card) => (
                <div className="account-identity" key={card.itemId}>
                  <span>{card.productName} · {card.skuName}</span>
                  <b className={card.summary.available + card.summary.reserved > 0 ? 'ok' : ''}>
                    可用 {card.summary.available} · 已预约 {card.summary.reserved} · 已用 {card.summary.consumed}
                    {card.expiresAt ? ` · 至 ${new Date(card.expiresAt).toLocaleDateString()}` : ''}
                  </b>
                </div>
              ))}
              {!cards.length && <p>还没有套餐卡；可在下方商城选购，付款后到店核销。</p>}
            </div>
            <div className="account-block">
              <h4>我的订单（{orders.length}）</h4>
              {orders.slice(0, 8).map((order) => (
                <div className="account-identity" key={order.id}>
                  <span>#{order.transactionNo} · {new Date(order.createdAt).toLocaleDateString()}</span>
                  <b>{orderStatusLabels[order.status] ?? order.status} · ¥{(order.cashTotalCents / 100).toFixed(2)}</b>
                </div>
              ))}
              {!orders.length && <p>还没有订单记录。</p>}
            </div>
            <div className="account-block">
              <h4>会员与积分</h4>
              <div className="account-identity">
                <span>当前等级</span>
                <b className="ok">
                  {membership?.level?.name ?? '普通会员'}
                  {membership?.level && membership.level.discountBps < 10000
                    ? `（${(membership.level.discountBps / 1000).toFixed(1)} 折）`
                    : ''}
                </b>
              </div>
              <div className="account-identity">
                <span>成长值</span><b>{membership?.growth?.value ?? 0}</b>
              </div>
              <div className="account-identity">
                <span>可用积分</span>
                <b className="ok">
                  {assets?.balance ?? 0}
                  {assets ? `（${assets.settings.redeemPoints} 分抵 ¥${(assets.settings.redeemCents / 100).toFixed(2)}）` : ''}
                </b>
              </div>
              <small>积分兑换、抽奖与邀请奖励在小程序内完成；官网结算已支持选券与积分抵扣。</small>
            </div>
            {!wechat && (
              <div className="account-block">
                <h4>关联微信小程序账号</h4>
                {link ? (
                  <>
                    <p>在小程序「我的 → 账户与安全」中粘贴下面的关联码完成关联（10 分钟内有效，只能用一次）：</p>
                    <code className="account-link-code">{link.id}.{link.token}</code>
                    <button type="button" className="account-secondary" onClick={() => void copyLinkCode()}>复制关联码</button>
                  </>
                ) : (
                  <button type="button" className="account-secondary" disabled={busy} onClick={() => void createLink()}>生成关联码</button>
                )}
              </div>
            )}
            <div className="account-block">
              <h4>注销账号</h4>
              {deactivation
                ? <p>{deactivationLabels[deactivation.status]}{deactivation.blockReason ? `：${deactivation.blockReason}` : ''}</p>
                : <button type="button" className="account-danger" disabled={busy} onClick={() => void requestDeactivation()}>申请注销</button>}
            </div>
            <button type="button" className="account-logout" onClick={() => void logout()}>退出登录</button>
          </div>
        )}

        {message && <p className="account-message">{message}</p>}
        {error && <p className="account-error" role="alert">{error}</p>}
      </div>
    </div>
  )
}
